import packageJson from '../package.json';
import type { WideEventsWrapper } from './wide-events';

/**
 * Detect the current environment using standard Node.js patterns
 */
function detectEnvironment(): string {
  // Check NODE_ENV first (standard Node.js environment variable)
  if (process.env.NODE_ENV) {
    return process.env.NODE_ENV;
  }

  // Check for Cloudflare environment indicators
  if (process.env.CF_ENVIRONMENT) {
    return process.env.CF_ENVIRONMENT;
  }

  // Check for other common environment indicators
  if (process.env.ENVIRONMENT) {
    return process.env.ENVIRONMENT;
  }

  // Check for development indicators
  if (
    process.env.NODE_ENV === undefined &&
    (process.env.DEVELOPMENT || process.env.DEV)
  ) {
    return 'development';
  }

  // Default to development if no environment is detected
  return 'development';
}

/**
 * Configuration for context schema - defines which fields are tracked for each category
 */
export interface ContextSchemaConfig {
  /** User-related context fields */
  user: string[];
  /** Entity-related context fields */
  entity: string[];
  /** Operation-related context fields */
  operation: string[];
  /** Infrastructure-related context fields */
  infrastructure: string[];
  /** Error-related context fields */
  error: string[];
  /** HTTP-related context fields */
  http: string[];
  /** Performance/timing-related context fields */
  performance: string[];
  /** Service-related context fields */
  service: string[];
}

/**
 * Error response pattern for automatic error detection and categorization
 */
export interface ErrorResponsePattern {
  /** Human-readable name for the error pattern */
  name: string;
  /** Function that tests if a response body matches this error pattern */
  matcher: (body: any) => boolean;
  /** Function that tracks the error with appropriate context and categorization */
  tracker: (wideEvent: WideEventsWrapper, body: any) => void;
}

/**
 * Tracing configuration options
 */
export interface TracingConfig {
  /** Whether to enable OpenTelemetry tracing */
  enabled: boolean;
  /** Service name for tracing */
  serviceName?: string;
  /** Service version for tracing */
  serviceVersion?: string;
  /** Environment name */
  environment?: string;
  /** Custom attributes to add to all spans */
  defaultAttributes?: Record<string, string | number | boolean>;
}

/**
 * HTTP tracking configuration
 */
export interface HttpTrackingConfig {
  /** Whether to automatically track HTTP request details */
  trackRequests: boolean;
  /** Whether to automatically track HTTP response details */
  trackResponses: boolean;
  /** Whether to track request/response bodies */
  trackBodies: boolean;
  /** Maximum body size to track (in bytes) */
  maxBodySize: number;
  /** Headers to exclude from tracking */
  excludeHeaders: string[];
  /** Headers to include in tracking (if specified, only these will be tracked) */
  includeHeaders?: string[];
}

/**
 * Error tracking configuration
 */
export interface ErrorTrackingConfig {
  /** Whether to automatically detect and track error responses */
  autoTrackErrorResponses: boolean;
  /** Whether to automatically categorize errors as expected/unexpected */
  autoCategorizeErrors: boolean;
  /** Custom error response patterns */
  errorPatterns: ErrorResponsePattern[];
  /** Default error categorization for unknown errors */
  defaultUnexpected: boolean;
}

/**
 * Performance tracking configuration
 */
export interface PerformanceConfig {
  /** Whether to automatically track operation timing */
  trackTiming: boolean;
  /** Whether to categorize performance (fast, slow, etc.) */
  categorizePerformance: boolean;
  /** Thresholds for performance categorization (in milliseconds) */
  performanceThresholds: {
    fast: number;
    slow: number;
  };
  /** Whether to automatically create spans for common operations */
  autoCreateSpans: boolean;
}

/**
 * Comprehensive observability configuration
 */
export interface ObservabilityConfig {
  /** Context schema configuration */
  contextSchema: ContextSchemaConfig;
  /** Tracing configuration */
  tracing: TracingConfig;
  /** HTTP tracking configuration */
  http: HttpTrackingConfig;
  /** Error tracking configuration */
  errors: ErrorTrackingConfig;
  /** Performance tracking configuration */
  performance: PerformanceConfig;
  /** Whether to log debug information */
  debug: boolean;
}

/**
 * Simplified options interface for backward compatibility
 */
export interface ObservabilityOptions {
  /** Custom service name (defaults to environment variable SERVICE_NAME) */
  serviceName?: string;
  /** Whether to automatically track HTTP request/response (defaults to true) */
  autoTrackHttp?: boolean;
  /** Whether to automatically detect and track error responses (defaults to true) */
  autoTrackErrorResponses?: boolean;
  /** Whether to enable debug logging (defaults to false) */
  debug?: boolean;
  /** Build info to initialize (optional) */
  buildInfo?: Record<string, string | number | boolean>;
}

/**
 * Default context schema configuration
 */
export const DEFAULT_CONTEXT_SCHEMA: ContextSchemaConfig = {
  user: ['id', 'email', 'first_name', 'last_name', 'actor_type'],
  entity: ['type', 'id', 'status'],
  operation: ['type', 'name', 'domain', 'layer'],
  infrastructure: [
    'event_store.stream_id',
    'event_store.expected_version',
    'event_store.next_version',
    'event_store.created_new_stream',
    'database.operation',
    'database.table',
    'database.duration_ms',
    'external_service.name',
    'external_service.operation',
    'external_service.duration_ms',
  ],
  error: [
    'type',
    'message',
    'unexpected',
    'validation.issue_count',
    'validation.fields',
    'business_rule.rule',
    'business_rule.violation',
  ],
  http: [
    'method',
    'path',
    'status_code',
    'user_agent',
    'content_type',
    'content_length',
  ],
  performance: [
    'duration_ms',
    'category',
    'timing.total_ms',
    'timing.domain_ms',
    'timing.validation_ms',
    'timing.event_store_ms',
    'timing.database_ms',
  ],
  service: ['name', 'version', 'environment', 'build_info'],
};

/**
 * Default error response patterns
 */
export const DEFAULT_ERROR_PATTERNS: ErrorResponsePattern[] = [
  {
    name: 'OpenAPIHono_ZodValidation',
    matcher: (body) =>
      body?.success === false &&
      body?.error?.name === 'ZodError' &&
      Array.isArray(body?.error?.issues),
    tracker: (wideEvent, body) => {
      wideEvent.trackError({
        type: 'ValidationError',
        message: 'Request validation failed',
        validation: {
          issueCount: body.error.issues.length,
          fields: body.error.issues.map((issue: any) =>
            Array.isArray(issue.path)
              ? issue.path.join('.')
              : String(issue.path)
          ),
        },
        unexpected: false, // Expected - user submitted invalid data
      });
    },
  },
  {
    name: 'BusinessValidation',
    matcher: (body) =>
      body?.success === false &&
      typeof body?.message === 'string' &&
      !body?.error, // No nested error object
    tracker: (wideEvent, body) => {
      wideEvent.trackError({
        type: 'BusinessValidationError',
        message: body.message,
        businessRule: {
          rule: 'business_validation',
          violation: body.message,
        },
        unexpected: false, // Expected - business rule violation
      });
    },
  },
  {
    name: 'GenericError',
    matcher: (body) =>
      body?.success === false ||
      body?.error ||
      typeof body?.message === 'string',
    tracker: (wideEvent, body) => {
      wideEvent.trackError({
        type: 'GenericError',
        message: body?.message || body?.error?.message || 'Unknown error',
        unexpected: true, // Unexpected - could be system issue
      });
    },
  },
];

/**
 * Default observability configuration
 */
export const DEFAULT_OBSERVABILITY_CONFIG: ObservabilityConfig = {
  contextSchema: DEFAULT_CONTEXT_SCHEMA,
  tracing: {
    enabled: true,
    serviceName: process.env.SERVICE_NAME || packageJson.name,
    serviceVersion: process.env.SERVICE_VERSION || packageJson.version,
    environment: detectEnvironment(),
    defaultAttributes: {},
  },
  http: {
    trackRequests: true,
    trackResponses: true,
    trackBodies: false,
    maxBodySize: 1024 * 1024, // 1MB
    excludeHeaders: ['authorization', 'cookie', 'x-api-key'],
    includeHeaders: undefined,
  },
  errors: {
    autoTrackErrorResponses: true,
    autoCategorizeErrors: true,
    errorPatterns: DEFAULT_ERROR_PATTERNS,
    defaultUnexpected: true,
  },
  performance: {
    trackTiming: true,
    categorizePerformance: true,
    performanceThresholds: {
      fast: 100,
      slow: 1000,
    },
    autoCreateSpans: true,
  },
  debug: false,
};

/**
 * Create observability configuration from simplified options
 */
export function createObservabilityConfig(
  options: ObservabilityOptions = {}
): ObservabilityConfig {
  const config = { ...DEFAULT_OBSERVABILITY_CONFIG };

  // Apply simple options to comprehensive config
  if (options.serviceName) {
    config.tracing.serviceName = options.serviceName;
  }

  if (options.autoTrackHttp !== undefined) {
    config.http.trackRequests = options.autoTrackHttp;
    config.http.trackResponses = options.autoTrackHttp;
  }

  if (options.autoTrackErrorResponses !== undefined) {
    config.errors.autoTrackErrorResponses = options.autoTrackErrorResponses;
  }

  if (options.debug !== undefined) {
    config.debug = options.debug;
  }

  return config;
}

/**
 * Utility class for managing observability configuration
 */
export class ObservabilityConfigManager {
  private config: ObservabilityConfig;

  constructor(config: ObservabilityConfig = DEFAULT_OBSERVABILITY_CONFIG) {
    this.config = { ...config };
  }

  /**
   * Get current configuration
   */
  getConfig(): ObservabilityConfig {
    return { ...this.config };
  }

  /**
   * Update configuration
   */
  updateConfig(updates: Partial<ObservabilityConfig>): void {
    this.config = { ...this.config, ...updates };
  }

  /**
   * Add custom context schema fields
   */
  addContextFields(
    category: keyof ContextSchemaConfig,
    fields: string[]
  ): void {
    this.config.contextSchema[category] = [
      ...this.config.contextSchema[category],
      ...fields,
    ];
  }

  /**
   * Add custom error pattern
   */
  addErrorPattern(pattern: ErrorResponsePattern): void {
    this.config.errors.errorPatterns.push(pattern);
  }

  /**
   * Remove error pattern by name
   */
  removeErrorPattern(name: string): void {
    this.config.errors.errorPatterns = this.config.errors.errorPatterns.filter(
      (pattern) => pattern.name !== name
    );
  }

  /**
   * Get context schema for a category
   */
  getContextSchema(category: keyof ContextSchemaConfig): string[] {
    return [...this.config.contextSchema[category]];
  }

  /**
   * Get all context schema fields (flattened)
   */
  getAllContextFields(): string[] {
    return Object.values(this.config.contextSchema).flat();
  }

  /**
   * Get error patterns
   */
  getErrorPatterns(): ErrorResponsePattern[] {
    return [...this.config.errors.errorPatterns];
  }
}

/**
 * Global configuration manager instance
 */
export const observabilityConfig = new ObservabilityConfigManager();

/**
 * Configure observability framework with simple options
 */
export function configureObservability(options: ObservabilityOptions): void {
  const config = createObservabilityConfig(options);
  observabilityConfig.updateConfig(config);
}

/**
 * Configure observability framework with comprehensive configuration
 */
export function configureObservabilityAdvanced(
  config: Partial<ObservabilityConfig>
): void {
  observabilityConfig.updateConfig(config);
}
